import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

// Not `data-hydration-key`: morphSection morphs every keyed element in a response, which would
// let this list and the product recommendations block in the same section overwrite each other.
const KEY_ATTRIBUTE = 'data-recently-viewed-key';

/**
 * Renders the products the visitor viewed earlier. Backs both the section and the block.
 */
class RecentlyViewedProducts extends Component {
  /** @type {string | undefined} */
  #excludedProductId;

  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    // Read once: the attribute is absent in the search context the list is re-rendered in.
    this.#excludedProductId = this.dataset.excludeProductId || undefined;

    if (this.#getProductIds().length === 0) {
      if (this.dataset.state !== 'onboarding') this.dataset.state = 'empty';
      return;
    }

    if (this.dataset.state === 'pending') this.dataset.state = 'loading';

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * @returns {string[]} The product ids to show, most recently viewed first.
   */
  #getProductIds() {
    const max = Number(this.dataset.maxProducts) || 4;

    return RecentlyViewed.getProducts()
      .filter((/** @type {string} */ id) => id !== this.#excludedProductId)
      .slice(0, max);
  }

  async #loadProducts() {
    const { sectionId } = this.dataset;
    const key = this.getAttribute(KEY_ATTRIBUTE);

    if (!sectionId || !key) throw new Error(`A section ID and a ${KEY_ATTRIBUTE} are required`);

    const productIds = this.#getProductIds();

    if (productIds.length === 0) {
      this.dataset.state = 'empty';
      return;
    }

    // Theme.routes.search_url carries the locale and market prefix.
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, !Shopify.designMode, url);
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const incoming = fragment.querySelector(`[${KEY_ATTRIBUTE}="${key}"]`);

      if (!incoming) throw new Error('The recently viewed products were not found in the response');

      // MORPH_OPTIONS is childrenOnly, so our own attributes and state survive.
      morph(this, incoming, MORPH_OPTIONS);
      this.#adoptStylesheet(fragment);

      this.dataset.state = 'ready';
    } catch (error) {
      console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
      this.dataset.state = 'empty';
      this.dataset.error = 'Error loading recently viewed products';
    }
  }

  /**
   * Adds the section stylesheet from the response, covering blocks that only render once products
   * are present. Added rather than replacing the live one, which holds styles for sibling blocks
   * that don't render on the search route.
   *
   * @param {Document} fragment - The parsed response.
   */
  #adoptStylesheet(fragment) {
    if (this.querySelector('style[data-recently-viewed-stylesheet]')) return;

    const stylesheet = fragment.querySelector('style[data-section-stylesheet]');
    if (!stylesheet) return;

    const style = document.createElement('style');
    style.setAttribute('data-recently-viewed-stylesheet', '');
    style.textContent = stylesheet.textContent;

    this.prepend(style);
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
